/**
 * Number and time formatting shared by every traffic block, so a value reads
 * the same in a tile, on an axis, in a tooltip and in the table twin. Three
 * spellings of one number on one surface is how a reader stops trusting any of
 * them.
 */

const BIT_UNITS = ['b/s', 'kb/s', 'Mb/s', 'Gb/s', 'Tb/s'];
const PACKET_UNITS = ['/s', 'k/s', 'M/s'];

function scale(value: number, units: string[]): string {
  const magnitude = Math.abs(value);
  let scaled = magnitude;
  let index = 0;
  while (scaled >= 1000 && index < units.length - 1) {
    scaled /= 1000;
    index += 1;
  }
  const sign = value < 0 ? '-' : '';
  const digits = scaled >= 100 || index === 0 ? 0 : 1;
  return `${sign}${scaled.toFixed(digits)} ${units[index]}`.replace(' /s', '/s');
}

/** Network rates are decimal — a gigabit is 10^9 bits, not 2^30. */
export function formatBits(value: number): string {
  return scale(value, BIT_UNITS);
}

export function formatPackets(value: number): string {
  return scale(value, PACKET_UNITS);
}

/**
 * A rate small enough to count. Errors and discards live here: three per second
 * is a finding and "0.0 /s" would hide it.
 */
export function formatCount(value: number): string {
  const magnitude = Math.abs(value);
  if (magnitude >= 1000) return `${Math.round(value).toLocaleString()}/s`;
  if (Number.isInteger(value)) return `${String(value)}/s`;
  return `${value.toFixed(1)}/s`;
}

export function formatPercent(value: number): string {
  return `${value >= 10 ? String(Math.round(value)) : value.toFixed(1)}%`;
}

/** Formats by the unit the server sent, rather than by guessing from the name. */
export function formatByUnit(value: number, unit: string): string {
  return unit === 'bits_per_second' ? formatBits(value) : formatPackets(value);
}

export function formatBucket(seconds: number): string {
  if (seconds < 60) return `${String(seconds)}s`;
  if (seconds < 3600) return `${String(Math.round(seconds / 60))} min`;
  return `${String(Math.round(seconds / 360) / 10)} h`;
}

/** Speed as an operator says it, from a rate in bits per second. */
export function formatLinkSpeed(bits: number): string {
  if (bits >= 1e9) return `${String(Math.round(bits / 1e9))} Gbps`;
  if (bits >= 1e6) return `${String(Math.round(bits / 1e6))} Mbps`;
  return `${String(Math.round(bits / 1e3))} kbps`;
}

/**
 * A count of things that happened, grouped for reading.
 *
 * Not `formatCount`, which is a *rate* and carries a `/s`. The distinction is
 * the whole point of the counter-endpoint reads: 412 errors and 412 errors per
 * second are four orders of magnitude apart.
 */
export function formatTotal(value: number): string {
  return Math.round(value).toLocaleString();
}
